import {
  googleDriveBrowserRuntime,
  type GoogleTokenClientFactory,
} from "@/lib/google-drive-picker";
import type { ImportRowInput } from "@/lib/api/imports";

export const GOOGLE_CLASSROOM_IMPORT_SCOPES = [
  "https://www.googleapis.com/auth/classroom.courses.readonly",
  "https://www.googleapis.com/auth/classroom.rosters.readonly",
  "https://www.googleapis.com/auth/classroom.profile.emails",
  "https://www.googleapis.com/auth/classroom.coursework.students.readonly",
].join(" ");

const CLASSROOM_API = "https://classroom.googleapis.com/v1";
const MAX_PAGES = 100;
export const GOOGLE_CLASSROOM_TOKEN_TIMEOUT_MS = 120_000;
const CANONICAL_SUBMISSIONS_SCOPE =
  "https://www.googleapis.com/auth/classroom.student-submissions.students.readonly";

export interface ClassroomCourseSummary {
  id: string;
  name: string;
  section: string | null;
  courseState: "ARCHIVED";
  creationTime: string | null;
  updateTime: string | null;
  gradebookSettings: {
    calculationType: string | null;
    displaySetting: string | null;
  } | null;
}

export interface ClassroomPerson {
  userId: string;
  fullName: string;
  emailAddress: string;
}

export interface ClassroomCourseWork {
  id: string;
  title: string;
  state: string;
  maxPoints: number | null;
  workType: string | null;
  creationTime: string | null;
  updateTime: string | null;
  dueDate: { year: number; month: number; day: number } | null;
  dueTime: { hours?: number; minutes?: number } | null;
}

export interface ClassroomSubmission {
  id: string;
  userId: string;
  courseWorkId: string;
  state: string;
  assignedGrade: number | null;
  draftGrade: number | null;
  late: boolean;
  creationTime: string | null;
  updateTime: string | null;
}

export interface ClassroomCourseSnapshot {
  capturedAt: string;
  course: ClassroomCourseSummary;
  teachers: ClassroomPerson[];
  students: ClassroomPerson[];
  courseWork: ClassroomCourseWork[];
  submissions: ClassroomSubmission[];
}

export interface ClassroomImportRuntime {
  loadIdentity: () => Promise<void>;
  requestToken: (clientId: string, scope: string) => Promise<ClassroomTokenResult>;
  fetch: (input: string, init: RequestInit) => Promise<Response>;
  now: () => Date;
}

export type ClassroomTokenResult =
  | {
    kind: "token";
    accessToken: string;
    expiresInSeconds: number;
    grantedScopes: string;
  }
  | { kind: "cancelled" }
  | { kind: "failed" };

interface ClassroomTokenResponse {
  access_token?: string;
  expires_in?: number;
  scope?: string;
  error?: string;
}

interface ClassroomTokenErrorResponse {
  type?: string;
}

function settleOnce<T>(
  timeoutMs: number,
  onTimeout: () => T,
  start: (settle: (value: T) => void) => void,
): Promise<T> {
  return new Promise<T>((resolve) => {
    let settled = false;
    const finish = (value: T) => {
      if (settled) return;
      settled = true;
      clearTimeout(timer);
      resolve(value);
    };
    const timer = setTimeout(() => finish(onTimeout()), timeoutMs);
    try {
      start(finish);
    } catch (error) {
      clearTimeout(timer);
      throw error;
    }
  });
}

function normalizedScopeSet(scope: string): Set<string> {
  return new Set(scope.split(/\s+/).filter(Boolean).map((value) => (
    value === CANONICAL_SUBMISSIONS_SCOPE
      ? "https://www.googleapis.com/auth/classroom.coursework.students.readonly"
      : value
  )));
}

function carriesExactClassroomScopes(scope: string): boolean {
  const granted = normalizedScopeSet(scope);
  const required = normalizedScopeSet(GOOGLE_CLASSROOM_IMPORT_SCOPES);
  return granted.size === required.size && [...required].every((value) => granted.has(value));
}

/**
 * Request the bounded Classroom token from the literal GIS seam.
 *
 * The explicit account selector is part of the authority boundary: the token
 * response has no stable user identifier under these four Classroom scopes,
 * so the human chooses the Google account once and every read in this page
 * session stays attached to the resulting bearer token.
 */
export function requestGoogleClassroomToken(
  oauth: GoogleTokenClientFactory,
  clientId: string,
  scope: string,
): Promise<ClassroomTokenResult> {
  return settleOnce<ClassroomTokenResult>(
    GOOGLE_CLASSROOM_TOKEN_TIMEOUT_MS,
    () => ({ kind: "failed" }),
    (settle) => {
      const client = oauth.initTokenClient({
        client_id: clientId,
        scope,
        include_granted_scopes: false,
        callback: (response: ClassroomTokenResponse) => {
          if (
            response.access_token
            && typeof response.expires_in === "number"
            && Number.isFinite(response.expires_in)
            && response.expires_in > 0
            && typeof response.scope === "string"
            && carriesExactClassroomScopes(response.scope)
          ) {
            settle({
              kind: "token",
              accessToken: response.access_token,
              expiresInSeconds: response.expires_in,
              grantedScopes: response.scope,
            });
            return;
          }
          settle({ kind: "failed" });
        },
        error_callback: (error?: ClassroomTokenErrorResponse) => {
          settle(error?.type === "popup_closed" ? { kind: "cancelled" } : { kind: "failed" });
        },
      });
      client.requestAccessToken({ prompt: "select_account" });
    },
  );
}

export const googleClassroomBrowserRuntime: ClassroomImportRuntime = {
  loadIdentity: googleDriveBrowserRuntime.loadIdentity,
  requestToken(clientId, scope) {
    const oauth = window.google?.accounts?.oauth2;
    if (!oauth) return Promise.resolve<ClassroomTokenResult>({ kind: "failed" });
    return requestGoogleClassroomToken(oauth, clientId, scope);
  },
  fetch: (input, init) => window.fetch(input, init),
  now: () => new Date(),
};

export class ClassroomImportError extends Error {}

function record(value: unknown): Record<string, unknown> {
  return value && typeof value === "object" && !Array.isArray(value)
    ? value as Record<string, unknown>
    : {};
}

function text(value: unknown): string {
  return typeof value === "string" ? value.trim() : "";
}

function optionalText(value: unknown): string | null {
  const found = text(value);
  return found || null;
}

function optionalNumber(value: unknown): number | null {
  return typeof value === "number" && Number.isFinite(value) ? value : null;
}

function parsePerson(value: unknown): ClassroomPerson | null {
  const row = record(value);
  const profile = record(row.profile);
  const name = record(profile.name);
  const userId = text(row.userId);
  const fullName = text(name.fullName);
  const emailAddress = text(profile.emailAddress).toLowerCase();
  if (!userId || !fullName || !emailAddress) return null;
  return { userId, fullName, emailAddress };
}

function parseCourse(value: unknown): ClassroomCourseSummary | null {
  const row = record(value);
  const id = text(row.id);
  const name = text(row.name);
  if (!id || !name || row.courseState !== "ARCHIVED") return null;
  const gradebook = record(row.gradebookSettings);
  return {
    id,
    name,
    section: optionalText(row.section),
    courseState: "ARCHIVED",
    creationTime: optionalText(row.creationTime),
    updateTime: optionalText(row.updateTime),
    gradebookSettings: Object.keys(gradebook).length === 0 ? null : {
      calculationType: optionalText(gradebook.calculationType),
      displaySetting: optionalText(gradebook.displaySetting),
    },
  };
}

function parseCourseWork(value: unknown): ClassroomCourseWork | null {
  const row = record(value);
  const id = text(row.id);
  const title = text(row.title);
  if (!id || !title) return null;
  const dueDate = record(row.dueDate);
  const dueTime = record(row.dueTime);
  const year = optionalNumber(dueDate.year);
  const month = optionalNumber(dueDate.month);
  const day = optionalNumber(dueDate.day);
  return {
    id,
    title,
    state: text(row.state),
    maxPoints: optionalNumber(row.maxPoints),
    workType: optionalText(row.workType),
    creationTime: optionalText(row.creationTime),
    updateTime: optionalText(row.updateTime),
    dueDate: year !== null && month !== null && day !== null ? { year, month, day } : null,
    dueTime: Object.keys(dueTime).length === 0 ? null : {
      hours: optionalNumber(dueTime.hours) ?? undefined,
      minutes: optionalNumber(dueTime.minutes) ?? undefined,
    },
  };
}

function parseSubmission(value: unknown): ClassroomSubmission | null {
  const row = record(value);
  const id = text(row.id);
  const userId = text(row.userId);
  const courseWorkId = text(row.courseWorkId);
  if (!id || !userId || !courseWorkId) return null;
  return {
    id,
    userId,
    courseWorkId,
    state: text(row.state),
    assignedGrade: optionalNumber(row.assignedGrade),
    draftGrade: optionalNumber(row.draftGrade),
    late: row.late === true,
    creationTime: optionalText(row.creationTime),
    updateTime: optionalText(row.updateTime),
  };
}

async function getJson(
  path: string,
  params: URLSearchParams,
  accessToken: string,
  runtime: ClassroomImportRuntime,
  signal?: AbortSignal,
): Promise<Record<string, unknown>> {
  const response = await runtime.fetch(`${CLASSROOM_API}${path}?${params}`, {
    method: "GET",
    headers: { Authorization: `Bearer ${accessToken}` },
    signal,
  });
  if (!response.ok) {
    throw new ClassroomImportError(
      response.status === 403
        ? "Google refused this Classroom read. Verify the tenant OAuth client, API enablement, and approved read-only scopes."
        : `Google Classroom returned HTTP ${response.status}`,
    );
  }
  const payload: unknown = await response.json();
  return record(payload);
}

async function paged(
  path: string,
  params: URLSearchParams,
  collection: string,
  accessToken: string,
  runtime: ClassroomImportRuntime,
  signal?: AbortSignal,
): Promise<unknown[]> {
  const rows: unknown[] = [];
  let pageToken = "";
  for (let page = 0; page < MAX_PAGES; page += 1) {
    const pageParams = new URLSearchParams(params);
    if (pageToken) pageParams.set("pageToken", pageToken);
    const payload = await getJson(path, pageParams, accessToken, runtime, signal);
    const values = payload[collection];
    if (Array.isArray(values)) rows.push(...values);
    pageToken = text(payload.nextPageToken);
    if (!pageToken) return rows;
  }
  throw new ClassroomImportError("Google Classroom pagination exceeded the safety ceiling");
}

export type ClassroomSessionInvalidationReason = "disconnect" | "expired" | "provider_refused";

type ClassroomSessionListener = (reason: ClassroomSessionInvalidationReason) => void;

/**
 * One authorization session for one mounted Mufid import page.
 *
 * The access token is deliberately private. React state receives only parsed
 * Classroom evidence, while this object sends the bearer token only to the
 * exact Google Classroom API origin. Reload or navigation destroys the object;
 * disconnect, expiry, and proven provider refusal overwrite it immediately.
 */
export class ClassroomImportSession {
  private authorization: { accessToken: string; expiresAt: number } | null = null;
  private expiryTimer: ReturnType<typeof setTimeout> | null = null;
  private connectionGeneration = 0;
  private catalogAbort: AbortController | null = null;
  private readonly listeners = new Set<ClassroomSessionListener>();

  constructor(
    private readonly clientId: string,
    private readonly runtime: ClassroomImportRuntime = googleClassroomBrowserRuntime,
  ) {}

  subscribe(listener: ClassroomSessionListener): () => void {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }

  private clearAuthorization(reason: ClassroomSessionInvalidationReason, notify = true): void {
    const hadAuthorization = this.authorization !== null || this.expiryTimer !== null;
    this.authorization = null;
    if (this.expiryTimer !== null) clearTimeout(this.expiryTimer);
    this.expiryTimer = null;
    if (notify && (hadAuthorization || reason === "disconnect")) {
      this.listeners.forEach((listener) => listener(reason));
    }
  }

  private installAuthorization(result: Extract<ClassroomTokenResult, { kind: "token" }>): void {
    this.clearAuthorization("disconnect", false);
    const lifetimeMs = result.expiresInSeconds * 1_000;
    this.authorization = {
      accessToken: result.accessToken,
      expiresAt: this.runtime.now().getTime() + lifetimeMs,
    };
    this.expiryTimer = setTimeout(() => this.clearAuthorization("expired"), lifetimeMs);
  }

  private assertCurrentConnection(generation: number): void {
    if (generation === this.connectionGeneration) return;
    const error = new Error("The Classroom connection ended before authorization completed");
    error.name = "AbortError";
    throw error;
  }

  private accessToken(): string {
    const current = this.authorization;
    if (!current || this.runtime.now().getTime() >= current.expiresAt) {
      this.clearAuthorization("expired");
      throw new ClassroomImportError("The Classroom connection expired. Connect Classroom again.");
    }
    return current.accessToken;
  }

  private async readCollection(
    path: string,
    params: URLSearchParams,
    collection: string,
    signal?: AbortSignal,
  ): Promise<unknown[]> {
    try {
      return await paged(path, params, collection, this.accessToken(), this.runtime, signal);
    } catch (error) {
      if (
        error instanceof ClassroomImportError
        && /HTTP 401|refused this Classroom read/.test(error.message)
      ) {
        this.clearAuthorization("provider_refused");
      }
      throw error;
    }
  }

  async connect(): Promise<ClassroomCourseSummary[]> {
    const generation = this.connectionGeneration + 1;
    this.connectionGeneration = generation;
    this.catalogAbort?.abort();
    const controller = new AbortController();
    this.catalogAbort = controller;
    this.clearAuthorization("disconnect", false);
    try {
      await this.runtime.loadIdentity();
      this.assertCurrentConnection(generation);
      const token = await this.runtime.requestToken(this.clientId, GOOGLE_CLASSROOM_IMPORT_SCOPES);
      this.assertCurrentConnection(generation);
      if (token.kind === "cancelled") {
        throw new ClassroomImportError("Google authorization was cancelled");
      }
      if (token.kind !== "token") {
        throw new ClassroomImportError("Google refused the Classroom authorization");
      }
      this.installAuthorization(token);
      const rows = await this.readCollection(
        "/courses",
        new URLSearchParams({
          courseStates: "ARCHIVED",
          pageSize: "100",
          fields: "nextPageToken,courses(id,name,section,courseState,creationTime,updateTime,gradebookSettings)",
        }),
        "courses",
        controller.signal,
      );
      this.assertCurrentConnection(generation);
      this.accessToken();
      this.catalogAbort = null;
      return rows.map(parseCourse).filter((course): course is ClassroomCourseSummary => course !== null);
    } catch (error) {
      if (generation === this.connectionGeneration) {
        this.catalogAbort = null;
        this.clearAuthorization("disconnect", false);
      }
      throw error;
    }
  }

  async hydrate(
    course: ClassroomCourseSummary,
    signal?: AbortSignal,
  ): Promise<ClassroomCourseSnapshot> {
    const accessToken = this.accessToken();
    const base = { pageSize: "100" };
    const [teachers, students, courseWork, submissions] = await Promise.all([
      this.readCollection(`/courses/${encodeURIComponent(course.id)}/teachers`, new URLSearchParams({
      ...base,
      fields: "nextPageToken,teachers(userId,profile(name(fullName),emailAddress))",
      }), "teachers", signal),
      this.readCollection(`/courses/${encodeURIComponent(course.id)}/students`, new URLSearchParams({
      ...base,
      fields: "nextPageToken,students(userId,profile(name(fullName),emailAddress))",
      }), "students", signal),
      this.readCollection(`/courses/${encodeURIComponent(course.id)}/courseWork`, new URLSearchParams({
      ...base,
      orderBy: "updateTime desc",
      fields: "nextPageToken,courseWork(id,title,state,maxPoints,workType,creationTime,updateTime,dueDate,dueTime)",
      }), "courseWork", signal),
      this.readCollection(
      `/courses/${encodeURIComponent(course.id)}/courseWork/-/studentSubmissions`,
      new URLSearchParams({
        ...base,
        fields: "nextPageToken,studentSubmissions(id,userId,courseWorkId,state,assignedGrade,draftGrade,late,creationTime,updateTime)",
      }),
      "studentSubmissions",
      signal,
    ),
  ]);
    // Recheck after all four reads. Expiry or refusal in one concurrent branch
    // invalidates the page session even if another branch happened to finish.
    if (this.accessToken() !== accessToken) {
      throw new ClassroomImportError("The Classroom connection changed while evidence was loading");
    }
    return {
      capturedAt: this.runtime.now().toISOString(),
      course,
      teachers: teachers.map(parsePerson).filter((person): person is ClassroomPerson => person !== null),
      students: students.map(parsePerson).filter((person): person is ClassroomPerson => person !== null),
      courseWork: courseWork.map(parseCourseWork).filter((work): work is ClassroomCourseWork => work !== null),
      submissions: submissions.map(parseSubmission).filter((row): row is ClassroomSubmission => row !== null),
    };
  }

  disconnect(): void {
    this.connectionGeneration += 1;
    this.catalogAbort?.abort();
    this.catalogAbort = null;
    this.clearAuthorization("disconnect");
  }
}

export function createClassroomImportSession(
  clientId: string,
  runtime: ClassroomImportRuntime = googleClassroomBrowserRuntime,
): ClassroomImportSession {
  return new ClassroomImportSession(clientId, runtime);
}

export interface ClassroomImportMapping {
  termName: string;
  courseCode: string;
  credits: number;
  instructors: ClassroomInstructorMapping[];
}

export interface ClassroomInstructorMapping {
  teacherUserId: string;
  facultyProfileId: string | null;
  createAccountlessRecord: boolean;
  assignmentKind: "primary" | "co_instructor";
}

export interface ClassroomImportSourceBatch {
  sourceName: string;
  payload: string;
  rows: ImportRowInput[];
  batchIndex: number;
  batchCount: number;
  learnerCount: number;
  submissionCount: number;
}

function compareStable(left: string, right: string): number {
  return left < right ? -1 : left > right ? 1 : 0;
}

function facultyImportKey(teacherUserId: string): string {
  return `google-classroom:teacher:${teacherUserId}`;
}

export function classroomMaximumStudents(teacherCount: number): number {
  return Math.max(0, Math.floor((100 - 2 - teacherCount) / 2));
}

// The governed source carries ONE evidence shape, snake_case, matching the rest
// of `source_ref`. Google's own camelCase is normalized here rather than in the
// projection that reads it: `coursework_evidence` used to ship the raw provider
// objects while `submission_evidence` was already snake_case, so a reader had to
// know which array it was holding before it could name a field. Issue #350's
// Course History projection reads one shape.
function courseworkEvidence(work: ClassroomCourseWork) {
  return {
    id: work.id,
    title: work.title,
    state: work.state,
    work_type: work.workType,
    max_points: work.maxPoints,
    creation_time: work.creationTime,
    update_time: work.updateTime,
    due_date: work.dueDate,
    due_time: work.dueTime,
  };
}

// `submission_id`, `creation_time`, and `update_time` are captured by
// `ClassroomSubmission` and were being dropped here. They are the only evidence
// that distinguishes one learner's attempt from a re-export of the same one, so
// the projection needs them to deduplicate without guessing.
function submissionEvidence(submission: ClassroomSubmission) {
  return {
    submission_id: submission.id,
    course_work_id: submission.courseWorkId,
    state: submission.state,
    assigned_grade: submission.assignedGrade,
    draft_grade: submission.draftGrade,
    late: submission.late,
    creation_time: submission.creationTime,
    update_time: submission.updateTime,
  };
}

function gradebookEvidence(settings: ClassroomCourseSummary["gradebookSettings"]) {
  return settings === null ? null : {
    calculation_type: settings.calculationType,
    display_setting: settings.displaySetting,
  };
}

export function buildClassroomImportSource(
  snapshot: ClassroomCourseSnapshot,
  mapping: ClassroomImportMapping,
  batch?: { index: number; count: number },
): { sourceName: string; payload: string; rows: ImportRowInput[] } {
  const termName = mapping.termName.trim();
  const courseCode = mapping.courseCode.trim();
  const teachersById = new Map(snapshot.teachers.map((teacher) => [teacher.userId, teacher]));
  const instructors = mapping.instructors.filter((instructor) => teachersById.has(instructor.teacherUserId));
  const hasOnePrimary = instructors.filter((instructor) => instructor.assignmentKind === "primary").length === 1;
  const allResolved = instructors.length === snapshot.teachers.length
    && new Set(instructors.map((instructor) => instructor.teacherUserId)).size === snapshot.teachers.length
    && instructors.every((instructor) => Boolean(instructor.facultyProfileId) !== instructor.createAccountlessRecord);
  if (!termName || !courseCode || !Number.isInteger(mapping.credits) || mapping.credits < 0 || !hasOnePrimary || !allResolved) {
    throw new ClassroomImportError("Term, course code, credits, and every Instructor record mapping with exactly one primary are required");
  }
  const termKey = `google-classroom:term:${termName.toLowerCase()}`;
  const courseKey = `google-classroom:course:${snapshot.course.id}`;
  const primaryFacultyKey = facultyImportKey(instructors.find((instructor) => instructor.assignmentKind === "primary")!.teacherUserId);
  const coInstructorFacultyKeys = instructors
    .filter((instructor) => instructor.assignmentKind === "co_instructor")
    .map((instructor) => facultyImportKey(instructor.teacherUserId));
  const rows: ImportRowInput[] = [
    {
      entity_kind: "term",
      import_key: termKey,
      source_ref: {
        provider: "google_classroom",
        course_id: snapshot.course.id,
        captured_at: snapshot.capturedAt,
        review_mapping: { term_name: termName },
      },
      data: { name: termName, status: "closed" },
    },
    {
      entity_kind: "course",
      import_key: courseKey,
      source_ref: {
        provider: "google_classroom",
        course_id: snapshot.course.id,
        source_name: snapshot.course.name,
        source_section: snapshot.course.section,
        course_state: snapshot.course.courseState,
        // The course ROW carries the capture and provider timestamps, not just
        // the term row and the payload envelope. Course History projects this
        // row and nothing above it, so evidence recorded only in the envelope is
        // evidence an administrator cannot see.
        captured_at: snapshot.capturedAt,
        source_creation_time: snapshot.course.creationTime,
        source_update_time: snapshot.course.updateTime,
        gradebook_settings: gradebookEvidence(snapshot.course.gradebookSettings),
        coursework_evidence: snapshot.courseWork.map(courseworkEvidence),
        review_mapping: {
          term_name: termName,
          course_code: courseCode,
          credits: mapping.credits,
          primary_faculty_key: primaryFacultyKey,
          co_instructor_faculty_keys: coInstructorFacultyKeys,
        },
      },
      data: {
        term_key: termKey,
        code: courseCode,
        title: snapshot.course.name,
        credits: mapping.credits,
        primary_faculty_key: primaryFacultyKey,
        co_instructor_faculty_keys: coInstructorFacultyKeys,
        status: "archived",
      },
    },
  ];
  for (const instructor of instructors) {
    const teacher = teachersById.get(instructor.teacherUserId)!;
    rows.push({
      entity_kind: "faculty",
      import_key: facultyImportKey(teacher.userId),
      source_ref: {
        provider: "google_classroom",
        google_user_id: teacher.userId,
        source_teacher_name: teacher.fullName,
        email_address: teacher.emailAddress,
        assignment_kind: instructor.assignmentKind,
        identity_policy: { automatic_match: false },
      },
      data: instructor.facultyProfileId
        ? { faculty_profile_id: instructor.facultyProfileId }
        : {
          display_name: teacher.fullName,
          account_link_state: "not_linked",
          portal_access: "none",
        },
    });
  }
  for (const student of snapshot.students) {
    const studentKey = `google-classroom:user:${student.userId}`;
    const enrollmentKey = `${courseKey}:user:${student.userId}`;
    rows.push({
      entity_kind: "student",
      import_key: studentKey,
      source_ref: {
        provider: "google_classroom",
        google_user_id: student.userId,
        email_address: student.emailAddress,
        source_roster_name: student.fullName,
        identity_policy: {
          automatic_match: false,
          learner_choice: "link_create_or_leave_out",
          generated_sid_namespace: "HIST-#####",
        },
      },
      data: {
        legal_name: student.fullName,
        email: student.emailAddress,
        status: "completed",
      },
    });
    rows.push({
      entity_kind: "enrollment",
      import_key: enrollmentKey,
      source_ref: {
        provider: "google_classroom",
        course_id: snapshot.course.id,
        google_user_id: student.userId,
        identity_policy: { generated_sid_namespace: "HIST-#####" },
        submission_evidence: snapshot.submissions
          .filter((submission) => submission.userId === student.userId)
          .map(submissionEvidence),
      },
      data: { student_key: studentKey, course_key: courseKey, status: "complete" },
    });
  }
  if (rows.length > 100) {
    throw new ClassroomImportError(`This course produces ${rows.length} rows; one governed import accepts 100`);
  }

  const source = {
    source_contract: "google-classroom-course-snapshot-v1",
    captured_at: snapshot.capturedAt,
    authority: "historical_evidence_only",
    ...(batch ? { batch: { index: batch.index, count: batch.count } } : {}),
    omissions: [
      "overall final grade: Classroom does not expose an authoritative certified course outcome",
      "credits: supplied by the operator because Classroom does not own institutional credit value",
      "attendance: not requested or inferred",
    ],
    classroom: snapshot,
    rows,
  };
  return {
    sourceName: `google-classroom-${snapshot.course.id}-${snapshot.capturedAt.slice(0, 10)}${batch ? `-batch-${String(batch.index).padStart(2, "0")}-of-${String(batch.count).padStart(2, "0")}` : ""}.json`,
    payload: JSON.stringify(source, null, 2),
    rows,
  };
}

export function buildClassroomImportSources(
  snapshot: ClassroomCourseSnapshot,
  mapping: ClassroomImportMapping,
): ClassroomImportSourceBatch[] {
  const orderedStudents = [...snapshot.students]
    .sort((left, right) => compareStable(left.userId, right.userId));
  const learnersPerBatch = classroomMaximumStudents(snapshot.teachers.length);
  if (orderedStudents.length > 0 && learnersPerBatch === 0) {
    throw new ClassroomImportError("The Classroom teacher roster leaves no room for learner rows within the 100-row import limit");
  }
  const learnerGroups = orderedStudents.length === 0
    ? [[]]
    : Array.from(
      { length: Math.ceil(orderedStudents.length / learnersPerBatch) },
      (_unused, index) => orderedStudents.slice(
        index * learnersPerBatch,
        (index + 1) * learnersPerBatch,
      ),
    );
  const batchCount = learnerGroups.length;
  return learnerGroups.map((students, offset) => {
    const studentIds = new Set(students.map((student) => student.userId));
    const batchSnapshot = {
      ...snapshot,
      students,
      submissions: snapshot.submissions
        .filter((submission) => studentIds.has(submission.userId))
        .sort((left, right) => (
          compareStable(left.userId, right.userId)
          || compareStable(left.courseWorkId, right.courseWorkId)
          || compareStable(left.id, right.id)
        )),
    };
    const batchIndex = offset + 1;
    const generated = buildClassroomImportSource(
      batchSnapshot,
      mapping,
      batchCount > 1 ? { index: batchIndex, count: batchCount } : undefined,
    );
    return {
      ...generated,
      batchIndex,
      batchCount,
      learnerCount: students.length,
      submissionCount: batchSnapshot.submissions.length,
    };
  });
}
